import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { MdMyLocation, MdLocationPin } from "react-icons/md";

const DEFAULT_LAT = 13.00707;
const DEFAULT_LNG = 80.262;
const LEAFLET_VERSION = "1.9.4";

// Load Leaflet CSS + JS from CDN once, shared across all instances
let leafletPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve();
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    // CSS first
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
    document.head.appendChild(css);

    // JS
    const script = document.createElement("script");
    script.src = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
    script.onload = resolve;
    script.onerror = () => { leafletPromise = null; reject(new Error("Leaflet load failed")); };
    document.head.appendChild(script);
  });

  return leafletPromise;
}

/**
 * LocationPicker — reusable, self-contained map location picker.
 *
 * Props:
 *   latitude   number | string | null
 *   longitude  number | string | null
 *   onChange   (lat: number, lng: number) => void
 *   disabled   boolean — read-only view mode
 */
export default function LocationPicker({ latitude, longitude, onChange, disabled }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);       // Leaflet map instance
  const markerRef = useRef(null);    // Leaflet marker instance
  const [ready, setReady] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");

  const startLat = latitude ? Number(latitude) : DEFAULT_LAT;
  const startLng = longitude ? Number(longitude) : DEFAULT_LNG;

  // Fix default Leaflet marker icon path when loaded from CDN
  function fixLeafletIcon() {
    const L = window.L;
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-icon.png`,
      iconRetinaUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-icon-2x.png`,
      shadowUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-shadow.png`,
    });
  }

  // Move marker + pan map (no re-init)
  const moveMarker = useCallback((lat, lng) => {
    if (!markerRef.current || !mapRef.current) return;
    const ll = [lat, lng];
    markerRef.current.setLatLng(ll);
    mapRef.current.panTo(ll);
  }, []);

  // Build the Leaflet map
  const setupMap = useCallback((lat, lng) => {
    if (!containerRef.current || mapRef.current) return; // guard double init
    const L = window.L;
    fixLeafletIcon();

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 15,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([lat, lng], { draggable: !disabled }).addTo(map);
    markerRef.current = marker;
    mapRef.current = map;

    if (!disabled) {
      // Drag pin → update coords
      marker.on("dragend", () => {
        const { lat: newLat, lng: newLng } = marker.getLatLng();
        if (onChange) onChange(newLat, newLng);
      });

      // Click map → move pin + update coords
      map.on("click", (e) => {
        const { lat: newLat, lng: newLng } = e.latlng;
        marker.setLatLng([newLat, newLng]);
        if (onChange) onChange(newLat, newLng);
      });
    }

    setReady(true);
  }, [disabled, onChange]);

  // Load Leaflet on mount, then init map
  useEffect(() => {
    loadLeaflet()
      .then(() => setupMap(startLat, startLng))
      .catch(() => setError("Could not load map. Check your internet connection."));

    return () => {
      // Cleanup map on unmount to avoid "container already initialised" error
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync marker when parent manually changes lat/lng inputs
  useEffect(() => {
    if (!ready || !latitude || !longitude) return;
    moveMarker(Number(latitude), Number(longitude));
  }, [latitude, longitude, ready, moveMarker]);

  // High-accuracy GPS fetch
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported by your browser.");
      return;
    }
    setLocating(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setLocating(false);
        moveMarker(lat, lng);
        if (onChange) onChange(lat, lng);
      },
      (err) => {
        setLocating(false);
        const msg = {
          1: "Location access denied. Please allow location in your browser settings.",
          2: "Location unavailable. Try again or pin manually on the map.",
          3: "Location timed out. Try again.",
        };
        setError(msg[err.code] || "Failed to get location.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  return (
    <div style={s.wrapper}>
      {/* ── Toolbar ── */}
      {!disabled && (
        <div style={s.toolbar}>
          <button
            type="button"
            style={s.btn}
            onClick={handleGetCurrentLocation}
            disabled={locating}
          >
            {locating ? (
              <>
                <span
                  style={s.spinner}
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                &nbsp;Getting location…
              </>
            ) : (
              <>
                <MdMyLocation size={17} style={s.btnIcon} />
              </>
            )}
          </button>

          <span style={s.hint}>
            <MdLocationPin size={14} style={{ color: "#e53935", marginRight: 3 }} />
            Tap on map or drag pin to place location
          </span>
        </div>
      )}

      {/* ── Error banner ── */}
      {error && <div style={s.errorBox}>{error}</div>}

      {/* ── Map ── */}
      <div style={s.mapWrap}>
        {/* Leaflet renders into this div */}
        <div ref={containerRef} style={s.mapEl} />

        {/* Loading overlay (removed once ready) */}
        {!ready && !error && (
          <div style={s.loadOverlay}>
            <span className="spinner-border spinner-border-sm text-secondary" />
            <span style={s.loadText}>Loading map…</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10,
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#1a73e8",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
    whiteSpace: "nowrap",
    lineHeight: 1.4,
  },
  btnIcon: {
    verticalAlign: "middle",
    flexShrink: 0,
  },
  spinner: {
    flexShrink: 0,
  },
  hint: {
    fontSize: 12,
    color: "#555",
    display: "flex",
    alignItems: "center",
  },
  errorBox: {
    fontSize: 12,
    color: "#b71c1c",
    background: "#ffebee",
    border: "1px solid #ef9a9a",
    borderRadius: 6,
    padding: "6px 12px",
  },
  mapWrap: {
    position: "relative",
    width: "100%",
    // Responsive height: 280px on small, 340px on large
    height: "clamp(240px, 40vw, 340px)",
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid #ccc",
    background: "#e8e8e8",
  },
  mapEl: {
    width: "100%",
    height: "100%",
  },
  loadOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "#e8e8e8",
    zIndex: 10,
  },
  loadText: {
    fontSize: 13,
    color: "#666",
  },
};

LocationPicker.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

LocationPicker.defaultProps = {
  latitude: null,
  longitude: null,
  onChange: null,
  disabled: false,
};
